import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { Observable } from 'rxjs';
import { IntuitiveIndication } from '../../../models/intuitive-indication';
import { toUnitTypeInBridge } from '../../../models/intuitive-indication';
import { ToothModalResult } from '../../../models/tooth-modal';
import { RxIntuitiveTeethDiagramQuery } from '../../../state/rx-intuitive-teeth-diagram-query';
import { RxIntuitiveTeethDiagramStore } from '../../../state/rx-intuitive-teeth-diagram-store';
import { RxIntuitiveCopyInBridgeService } from '../../rx-intuitive-copy-in-bridge.service';
import { RxIntuitiveToothEditorRoter } from '../../rx-intuitive-tooth-editor-roter.service';
import { IndicationOperations } from '../indication-context.token';

/**
 * Implements {@link IndicationOperations} for the bridge teeth diagram context.
 * Provided in {@link RxIntuitiveTeethBridgeOrchestratorComponent} and consumed by
 * {@link BridgeIndicationStrategy} and {@link ImplantBridgeIndicationStrategy} via the
 * {@link INDICATION_OPERATIONS} token.
 */
@Injectable()
export class BridgeIndicationService implements IndicationOperations {
	private readonly moduleName = 'BridgeIndicationService';

	constructor(
		private readonly intuitiveQuery: RxIntuitiveTeethDiagramQuery,
		private readonly intuitiveStore: RxIntuitiveTeethDiagramStore,
		private readonly sharingService: RxIntuitiveCopyInBridgeService,
		private readonly editorRouting: RxIntuitiveToothEditorRoter,
		private readonly logger: LoggerService
	) {}

	assignIndication(indicationId: IntuitiveIndication, skipResetTooth = false): void {
		const selectedToothIds = this.intuitiveQuery.selectedToothIds;

		if (selectedToothIds.length === 0) {
			this.logger.warn('assignIndication called with no teeth selected', {
				module: this.moduleName
			});

			return;
		}

		const unitTypeInBridge = toUnitTypeInBridge(indicationId);

		if (unitTypeInBridge == null) {
			return;
		}

		if (skipResetTooth) {
			this.intuitiveStore.updateBridgeTeethByIds(selectedToothIds, { ToothInBridgeTypeID: unitTypeInBridge });
		} else {
			this.intuitiveStore.assignBridgeIndication(selectedToothIds, unitTypeInBridge);
		}

		const updates = this.sharingService.computeBridgeIndicationSharingUpdates(
			selectedToothIds,
			unitTypeInBridge,
			this.intuitiveQuery.selectedBridgeTeeth
		);

		for (const { toothId, resetTooth } of updates) {
			this.intuitiveStore.updateBridgeTeethByIds([toothId], resetTooth);
		}

		this.intuitiveStore.markRecentlyEditedTeeth(selectedToothIds);
	}

	openModalForUnitTypeIfNeeded(indicationId: IntuitiveIndication): Observable<ToothModalResult> | null {
		return this.editorRouting.openBridgeModalForIndicationIfNeeded(indicationId, {
			onSave: (toothIds, partialUpdate) => {
				this.intuitiveStore.updateBridgeTeethByIds(toothIds, partialUpdate);
				this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
			},
			onDelete: toothIds => {
				this.intuitiveStore.updateBridgeTeethByIds(toothIds, { ToothInBridgeTypeID: undefined });
				this.intuitiveStore.markRecentlyEditedTeeth(toothIds);
			}
		});
	}
}
